import graphql__b54864b56fecd9fcac35 from "./__generated__/HeaderWithComplexMenuMobile_platform.graphql.js";
import graphql__b9854716d4a5a6961334 from "./__generated__/HeaderWithComplexMenuMobile_authenticationConfig.graphql.js";
import graphql__5eec1e51d734d494fd9e from "./__generated__/HeaderWithComplexMenuMobile_viewer.graphql.js";
import graphql__b1ccbcc398adae5c6a26 from "./__generated__/HeaderWithComplexMenuMobile_navigationItems.graphql.js";import { useCallback, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import Icon from "../Utils/Icon";
import Button from "../Buttons/Button";
import ButtonLink from "../Buttons/ButtonLink";
import HeaderAuthenticatedUser from "./HeaderAuthenticatedUser";
import ComplexMenuMobileMainNavigation from "../MainNavigation/ComplexMenu/Mobile/ComplexMenuMobileMainNavigation";
import { useRoutesContext } from "../Routes/RoutesContext";
import { useFormatMessage } from "../Intl/Context";
import { setBackdropOpen } from "../../reducers/styling";
import { intlMessages as headerIntlMessages } from "./Header";
import {
  ComplexMenuContext,
  useComplexMenuProps
} from "../MainNavigation/ComplexMenu/ComplexMenuContext";
import { useAffixHeaderOffsetTopUpdater } from "../Utils/useAffixHeaderOffsetTopUpdater";
import { useAppContainer } from "../Main/AppContainerContext";
import { useStickyElementsHeight } from "../Utils/StickyElementsHeight";

const HeaderWithComplexMenuMobile = props => {
  const platform = useFragment(HeaderWithComplexMenuMobile_platform, props.platform);
  const authenticationConfig = useFragment(
    HeaderWithComplexMenuMobile_authenticationConfig,
    props.authenticationConfig
  );
  const viewer = useFragment(HeaderWithComplexMenuMobile_viewer, props.viewer);
  const navigationItems = useFragment(
    HeaderWithComplexMenuMobile_navigationItems,
    props.navigationItems
  );

  const headerRef = useRef();

  const dispatch = useDispatch();
  const menuIsOpen = useSelector(({ styling }) => styling.isBackdropOpen);
  const pushableContainersOffset = useSelector(
    ({ styling }) => styling.pushableContainersOffset
  );

  const {
    logos,
    title: platformTitle,
    logoBackgroundColor,
    enableComplexMenu
  } = platform;

  const appContainer = useAppContainer();

  const { enableUserAccounts } = authenticationConfig;
  const { pathFor } = useRoutesContext();

  const t = useFormatMessage();
  const homeLabel = t(headerIntlMessages.home);
  const menuAriaLabel = t(headerIntlMessages.mainMenu);
  const menuBurgerLabel = t(headerIntlMessages.pageList);

  const handleToggleMenu = useCallback(() => {
    dispatch(setBackdropOpen(!menuIsOpen));
  }, [dispatch, menuIsOpen]);

  // This makes the navigation menu act as a modal and ensures the focus is trapped inside
  // the visible elements, which are the header, navigation and a possible alert message.
  useEffect(() => {
    if (!headerRef.current || !appContainer) return;

    const mainContainer = appContainer.querySelector("[data-main-container]");
    const footerContainer = appContainer.querySelector("[data-footer-container]");

    if (mainContainer) {
      if (menuIsOpen && !mainContainer.classList.contains("d-none")) {
        mainContainer.classList.add("d-none");
      } else if (!menuIsOpen && mainContainer.classList.contains("d-none")) {
        mainContainer.classList.remove("d-none");
      }
    }

    if (footerContainer) {
      if (menuIsOpen && !footerContainer.classList.contains("d-none")) {
        footerContainer.classList.add("d-none");
      } else if (!menuIsOpen && footerContainer.classList.contains("d-none")) {
        footerContainer.classList.remove("d-none");
      }
    }
  }, [appContainer, menuIsOpen]);

  const complexMenuProps = useComplexMenuProps(navigationItems);

  useAffixHeaderOffsetTopUpdater(() => {
    return headerRef.current?.offsetHeight || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef.current]);

  const alertHeight = useStickyElementsHeight({
    ignoreTopNavBar: true,
    ignoreStickyTitleBar: true
  });
  const headerHeight = headerRef.current?.offsetHeight || 0;
  const backdropOffsetHeight = alertHeight + headerHeight;

  const backdropHeight = headerRef.current && `calc(100vh - ${backdropOffsetHeight}px)`;

  const platformAltText = [platformTitle, homeLabel].join(", ");
  const homePath = pathFor("home");

  const headerClassName = classNames(
    "sticky-top",
    "header-mobile-global-container-navbar",
    {
      "position-absolute": menuIsOpen,
      "w-100": menuIsOpen
    }
  );

  const iconMenuBackdropClassName = classNames([
    "text-icon-xl",
    "fa",
    { "fa-times": menuIsOpen },
    { "fa-bars": !menuIsOpen }
  ]);

  const buttonLinkClassName = classNames([
    "navbar-brand",
    "d-flex",
    "justify-content-center",
    { "flex-1": !enableUserAccounts }
  ]);

  return (
    <ComplexMenuContext.Provider value={complexMenuProps}>
      <header
        role="banner"
        ref={headerRef}
        className={headerClassName}
        style={{ top: pushableContainersOffset }}
      >
        <nav
          className="navbar bg-white navbar-fixed-top header-nav-mobile-navbar"
          role="navigation"
          aria-label={menuAriaLabel}
        >
          <Button
            aria-label={menuBurgerLabel}
            onClick={handleToggleMenu}
            className="header-complex-nav-mobile-burger"
            aria-expanded={menuIsOpen}
          >
            <Icon className={iconMenuBackdropClassName} />
          </Button>

          <ButtonLink
            href={homePath}
            className={buttonLinkClassName}
            style={{ backgroundColor: logoBackgroundColor }}
          >
            <img src={logos.header} alt={platformAltText} className="navbar-brand-logo" />
          </ButtonLink>

          <HeaderAuthenticatedUser
            viewer={viewer}
            authenticationConfig={authenticationConfig}
            enableComplexMenu={enableComplexMenu}
          />
        </nav>

        {menuIsOpen && (
          <div
            className="navigation-complex-container-panel bg-primary margin-bottom-sm padding-md"
            style={{ height: backdropHeight }}
          >
            <ComplexMenuMobileMainNavigation
              navigationItems={navigationItems}
              viewer={viewer}
              authenticationConfig={authenticationConfig}
              platform={platform}
            />
          </div>
        )}
      </header>
    </ComplexMenuContext.Provider>
  );
};

const HeaderWithComplexMenuMobile_platform = graphql__b54864b56fecd9fcac35;

const HeaderWithComplexMenuMobile_authenticationConfig = graphql__b9854716d4a5a6961334;

const HeaderWithComplexMenuMobile_viewer = graphql__5eec1e51d734d494fd9e;

const HeaderWithComplexMenuMobile_navigationItems = graphql__b1ccbcc398adae5c6a26;

export default HeaderWithComplexMenuMobile;
